/**
 * UpgradeSettings — Upgrade Plan card + Upgrade dialog.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { TrendingUp, Check, Loader2 } from 'lucide-react';

export default function UpgradeSettings({
  restaurantGroup,
  subscriptionStatus,
  PLANS,
  upgradeDialogOpen,
  setUpgradeDialogOpen,
  handleUpgrade,
  submitting,
}) {
  if (restaurantGroup?.plan_type !== 'single') return null;

  return (
    <>
      {/* Upgrade Plan Card */}
      <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp size={20} className="text-orange-500" />
            Upgrade Your Plan
          </CardTitle>
          <CardDescription>
            Get premium features with Multi-Location plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-slate-900">
                  ${PLANS.multi?.monthlyPrice}
                </span>
                <span className="text-slate-500">/month</span>
                <span className="text-sm text-slate-400">or ${PLANS.multi?.yearlyPrice}/year</span>
              </div>
              <ul className="mt-3 space-y-2">
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Check size={16} className="text-green-500" />
                  Priority support
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Check size={16} className="text-green-500" />
                  All features included
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Check size={16} className="text-green-500" />
                  Priority support
                </li>
              </ul>
            </div>
            <Button
              onClick={() => setUpgradeDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="upgrade-plan-btn"
            >
              Upgrade Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade to Multi-Location</DialogTitle>
            <DialogDescription>
              Get premium features for your business
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Multi-Location Plan</p>
                  <p className="text-sm text-slate-500">Priority support &amp; all features</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900">
                    ${subscriptionStatus?.billing_cycle === 'yearly'
                      ? PLANS.multi?.yearlyPrice
                      : PLANS.multi?.monthlyPrice}
                  </p>
                  <p className="text-sm text-slate-500">
                    /{subscriptionStatus?.billing_cycle === 'yearly' ? 'year' : 'month'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Prorated billing:</strong> You&apos;ll only pay the difference for the remainder of your current billing period.
                {subscriptionStatus?.billing_cycle === 'yearly'
                  ? ` That's $${(PLANS.multi?.yearlyPrice || 0) - (PLANS.single?.yearlyPrice || 0)} more per year.`
                  : ` That's $${(PLANS.multi?.monthlyPrice || 0) - (PLANS.single?.monthlyPrice || 0)} more per month.`}
              </p>
            </div>

            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <Check size={16} className="text-green-500" />
                Unlimited locations
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <Check size={16} className="text-green-500" />
                All features included
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <Check size={16} className="text-green-500" />
                Changes take effect immediately
              </li>
            </ul>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setUpgradeDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpgrade}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                disabled={submitting}
                data-testid="confirm-upgrade-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Upgrade'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
